import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";
import {
  FilterTestRow,
  PromptAuditEntry,
  PromptDesign,
} from "../custom-behavior-prompts/types";

function baseUrl(companyUuid: string): string {
  return `${API_URL}/company/${companyUuid}/custom-behavior-prompts`;
}

export interface GeneratePromptInput {
  companyUuid: string;
  sourceText: string;
  customerIntent?: string;
  samplePosts?: string[];
  inputSource?: string;
  sourceFilename?: string;
}

export function useGeneratePrompt() {
  const { requestApi } = useAppContext();

  const generate = async (
    input: GeneratePromptInput
  ): Promise<PromptDesign> => {
    return requestApi
      .post(`${baseUrl(input.companyUuid)}/generate`, {
        source_text: input.sourceText,
        customer_intent: input.customerIntent ?? "",
        sample_posts: input.samplePosts ?? [],
        input_source: input.inputSource ?? "text",
        source_filename: input.sourceFilename ?? null,
      })
      .then((response) => response.data as PromptDesign);
  };

  return useMutation((input: GeneratePromptInput) => generate(input), {});
}

export interface TestPromptInput {
  companyUuid: string;
  promptPayload: string;
  posts: string[];
}

export function useTestPrompt() {
  const { requestApi } = useAppContext();

  const test = async (input: TestPromptInput): Promise<FilterTestRow[]> => {
    return requestApi
      .post(`${baseUrl(input.companyUuid)}/test`, {
        prompt_payload: input.promptPayload,
        posts: input.posts,
      })
      .then((response) => (response.data?.results ?? []) as FilterTestRow[]);
  };

  return useMutation((input: TestPromptInput) => test(input), {});
}

export interface SavePromptInput {
  companyUuid: string;
  promptPayload: string;
  positiveName?: string;
  negativeName?: string;
  autoRemove?: boolean;
  designSessionId?: string;
}

export function useSavePrompt() {
  const { requestApi } = useAppContext();

  const save = async (
    input: SavePromptInput
  ): Promise<Record<string, unknown>> => {
    return requestApi
      .put(`${baseUrl(input.companyUuid)}`, {
        prompt_payload: input.promptPayload,
        positive_name: input.positiveName ?? "",
        negative_name: input.negativeName ?? "",
        auto_remove: input.autoRemove ?? true,
        design_session_id: input.designSessionId ?? null,
      })
      .then((response) => response.data);
  };

  return useMutation((input: SavePromptInput) => save(input), {});
}

export interface ActivePrompt {
  external_id?: string;
  prompt_payload?: string;
  positive_name?: string;
  negative_name?: string;
  auto_remove?: boolean;
}

export function useActivePrompt(companyUuid: string | undefined) {
  const { requestApi } = useAppContext();

  return useQuery<ActivePrompt | null>(
    ["custom-behavior-prompt-active", companyUuid],
    async () => {
      if (!companyUuid) return null;
      return requestApi
        .get(`${baseUrl(companyUuid)}`)
        .then(
          (response) =>
            (response.data?.active_prompt ?? null) as ActivePrompt | null
        );
    },
    { enabled: Boolean(companyUuid), retry: 1 }
  );
}

export interface ExtractTextResult {
  source_text: string;
  extractor_used: string;
  char_count: number;
  warnings: string[];
  source_filename?: string;
}

export interface ExtractTextInput {
  companyUuid: string;
  file: File;
}

export function useExtractText() {
  const { requestApi } = useAppContext();

  const extract = async (
    input: ExtractTextInput
  ): Promise<ExtractTextResult> => {
    const formData = new FormData();
    formData.append("file", input.file);
    return requestApi
      .post(`${baseUrl(input.companyUuid)}/extract-text`, formData)
      .then((response) => response.data as ExtractTextResult);
  };

  return useMutation((input: ExtractTextInput) => extract(input), {});
}

export function usePromptAuditLog(companyUuid: string | undefined) {
  const { requestApi } = useAppContext();

  return useQuery<PromptAuditEntry[]>(
    ["custom-behavior-prompt-audit", companyUuid],
    async () => {
      if (!companyUuid) return [];
      return requestApi
        .get(`${baseUrl(companyUuid)}/audit-log`)
        .then(
          (response) => (response.data?.results ?? []) as PromptAuditEntry[]
        );
    },
    { enabled: Boolean(companyUuid), retry: 1 }
  );
}
